import { useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { useRouter } from "../lib/router.js";
import { useSession, type SessionUser } from "../lib/session.js";
import { Logo } from "../components/ui.js";

/**
 * One-time CREATE ADMINISTRATOR screen, shown only on a completely fresh
 * install (zero users). The first account created becomes the administrator;
 * afterwards this screen is never reachable and there is no public
 * registration.
 */
export function BootstrapPage() {
  const { setUser } = useSession();
  const { navigate } = useRouter();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.post<{ user: SessionUser }>("/api/auth/bootstrap", {
        username,
        password,
        displayName: displayName || undefined,
        email: email || undefined
      });
      setUser(result.user);
      navigate("/today", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <div className="auth-brand">
          <Logo size={88} />
          <span className="auth-tagline">Build the body. Build the system.</span>
        </div>
        <h1 className="auth-title">Create your administrator</h1>
        <p className="auth-sub">
          ClovaForm is private and invite-only. This first account becomes the
          administrator — after this, registration is closed and new people join
          by invitation only.
        </p>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="displayName">Display name (optional)</label>
            <input
              id="displayName"
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
            />
          </div>
          <div className="field">
            <label htmlFor="email">Email (optional)</label>
            <input
              id="email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
            />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <span className="hint">At least 10 characters.</span>
          </div>
          <div className="field">
            <label htmlFor="confirm">Confirm password</label>
            <input
              id="confirm"
              className="input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <button className="btn btn-primary btn-block" type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create administrator"}
          </button>
        </form>
      </div>
    </div>
  );
}
